import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import Remove from '@mui/icons-material/Remove';
import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListCountriesQuery } from '../../../../../../../../graphql/fragment/audiences/ListCountries.generated';
import type { ListRegionsQuery } from '../../../../../../../../graphql/fragment/audiences/ListRegions.generated';
import type { FormSegment } from '../../../../../../../../modules/audiences/AudienceForm/types';
import { daysToExpiration } from '../../../../../../utils/daysToExpiration';
import { isSegmentExpired, isSegmentSoonToExpire } from '../../../../../utils/hasExpiredSegments';
import { SegmentCategory } from '../../../hooks/types';
import { useReferenceData } from '../../../hooks/useReferenceData';
import { BasicInfo } from '../../drawer/components/SegmentListItem/BasicInfo';
import { MarketMetadata } from '../../drawer/components/SegmentListItem/MarketMetadata';
import { createSegmentMetadata } from '../../drawer/components/SegmentsList/utils/createSegmentMetadata';
import { formatReachValue } from '../../drawer/components/SegmentsList/utils/formatReachValue';
import { PredefinedChipIcon } from '../../icons/PredefinedChipIcon';
import { createBasicInfoPropsFromSegment } from '../../utils/createBasicInfoProps';
import { getExpireSoonTitle } from '../../utils/getExpireSoonTitle';
import {
  boxIconContainerStyles,
  closeIconStyles,
  containerStyles,
  headerRowStyles,
} from '../../utils/segmentListItemStyles';

export type SegmentListItemProps = {
  segment: FormSegment;
  isEdit: boolean;
  onDelete: () => void;
  regions: ListRegionsQuery['region'];
  countries: ListCountriesQuery['country'];
};

export const SegmentListItem = ({ segment, isEdit, onDelete, regions, countries }: SegmentListItemProps) => {
  const { t } = useTranslation();
  const { organizations } = useReferenceData();
  const metadata = useMemo(() => createSegmentMetadata(segment, t, segment.account_id || ''), [segment, t]);
  const hasMarketMetadata = metadata.regionCodes.length > 0 || metadata.countryCodes.length > 0;

  const isPredefinedSegment = segment.category === SegmentCategory.PREDEFINED;

  const basicInfoProps = useMemo(
    () =>
      createBasicInfoPropsFromSegment({
        segment,
        id: segment.id,
        reach: segment.reach,
        displayReach: formatReachValue(t, segment.reach),
        category: segment.category || '',
        organizationName: segment.account_id ? organizations[segment.account_id] : t('N / A'),
        providerName: null,
        isPredefinedSegment,
      }),
    [segment, t, organizations, isPredefinedSegment]
  );

  const checkIfSegmentIsExpired = useMemo(() => {
    return isSegmentExpired(segment);
  }, [segment]);

  const checkIfSegmentIsSoonToExpire = useMemo(() => {
    return isSegmentSoonToExpire(segment);
  }, [segment]);

  const renderCorrectIcon = () => {
    if (checkIfSegmentIsExpired) {
      return (
        <Tooltip title={t('Segment data is expired and cannot be used for audience creation')}>
          <ErrorIcon sx={{ mr: 1.5 }} data-test="error-icon" color="error" fontSize="small" />
        </Tooltip>
      );
    }
    if (checkIfSegmentIsSoonToExpire) {
      return (
        <Tooltip title={getExpireSoonTitle(t, daysToExpiration(segment.expiration_date) ?? 0)}>
          <Warning sx={{ mr: 1.5 }} data-test="warning-icon" color="warning" fontSize="small" />
        </Tooltip>
      );
    }
    return <CheckCircleOutlined sx={{ mr: 1.5 }} data-test="check-icon" color="success" fontSize="small" />;
  };

  return (
    <Box sx={containerStyles} data-test="segment-item">
      <Box sx={headerRowStyles}>
        {renderCorrectIcon()}

        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          {segment.category === SegmentCategory.PREDEFINED && (
            <Box sx={{ minWidth: 'fit-content', display: 'inline-flex' }} role="img" aria-label="Predefined segment">
              <PredefinedChipIcon />
            </Box>
          )}
          <Typography
            variant="body1Bold"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {segment.name}
          </Typography>
        </Box>
      </Box>

      <BasicInfo {...basicInfoProps} />
      {hasMarketMetadata && (
        <MarketMetadata
          regionCodes={metadata.regionCodes}
          countryCodes={metadata.countryCodes}
          regions={regions}
          countries={countries}
          dataTest="criteria-segment-market-metadata"
        />
      )}
      {isEdit && (
        <Box sx={boxIconContainerStyles} className="icon-container">
          <IconButton size="small" onClick={onDelete} className="close-icon" sx={closeIconStyles}>
            <Remove data-test="close-icon" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
